import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'

import { SlotStatus } from '@customTypes/Generic'

import getAppointmentEnd from 'src/helpers/getAppointmentEnd'

import useAvailabilities from './useAvailabilities'
import useComponentDidMount from '../useComponentDidMount'
import { useRecallQueryParams } from '../useQueryParams'

import captureExceptionWithResponse from 'src/services/sentry'

import { useConfig } from '@context/ConfigurationContext'
import { useRescheduleState, useRescheduleDispatch, setSlotStatus } from '@context/RescheduleContext'
import { useChoiceState } from '@context/ChoiceContext'

import { omit } from 'lodash'
import { usePractitionersContext } from '@context/PractitionersContext'
import { useHasUserSelectedSlot } from '../useHasUserSelectedSlot'

type QueryParamAppointment = {
  start?: string
  duration?: string | number
}

type UseValidateSelectedSlot = (
  queryParamAppointment: QueryParamAppointment,
  optional?: {
    onError?: () => void
  }
) => { isLoading: boolean; shouldRedirect: boolean }

const useValidateSelectedSlot: UseValidateSelectedSlot = (queryParamAppointment, optional) => {
  const { clinicId } = useConfig()
  const { selectedBookableDateAndTime, preferredPractitionerReferenceIds } = useChoiceState()
  const { slotStatus } = useRescheduleState()
  const rescheduleDispatch = useRescheduleDispatch()
  const recallQueryParams = useRecallQueryParams()
  const { isLoading: practitionersLoading } = usePractitionersContext()

  const now = moment.utc()
  const start = moment.utc(queryParamAppointment.start)
  const end = getAppointmentEnd(queryParamAppointment.start, queryParamAppointment.duration)
  const userSelectedSlotManually = useHasUserSelectedSlot()
  const startIsNotInQueryAndUserDidntSelectSlot = !queryParamAppointment.start && !userSelectedSlotManually

  /**
   * We will need to check slots that were not checeked at all or slots that were unavailable
   * The unavailable slots have to be rechecked as it's possible the user has selected a new one
   */

  const shouldCheckSlot =
    (slotStatus === SlotStatus.NOT_CHECKED || slotStatus === SlotStatus.UNAVAILABLE) && queryParamAppointment.start

  const isInPast = useMemo(() => {
    return userSelectedSlotManually
      ? moment.utc(`${selectedBookableDateAndTime?.day}T${selectedBookableDateAndTime?.time}`).isBefore(now)
      : moment.utc(queryParamAppointment.start).isBefore(now)
  }, [userSelectedSlotManually, now, queryParamAppointment.start, selectedBookableDateAndTime])

  const [shouldRedirect, setShouldRedirect] = useState(isInPast || startIsNotInQueryAndUserDidntSelectSlot)

  const { data, isLoading } = useAvailabilities(
    {
      start: queryParamAppointment.start,
      end,
      disabled: isInPast || userSelectedSlotManually || startIsNotInQueryAndUserDidntSelectSlot,
      preferredPractitionerReferenceIds,
    },
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: async (err: any) => {
        const { params, url } = err?.config || {}

        const requestParams = JSON.stringify(params || {})
        const responseData = JSON.stringify(err?.response?.data || {})

        const fetchAvailabilitiesError = {
          message: err.message,
          clinicId,
          queryParams: {
            ...omit(recallQueryParams, 'customerLastName'),
          },
          request: {
            requestParams,
            url,
            responseData,
          },
        }

        captureExceptionWithResponse('Recall: Error fetching availabilities', fetchAvailabilitiesError)

        optional?.onError?.()
      },
    }
  )

  useComponentDidMount(() => {
    if (isInPast) {
      rescheduleDispatch(setSlotStatus(SlotStatus.UNAVAILABLE))
    }
  })

  useEffect(() => {
    setShouldRedirect(isInPast || startIsNotInQueryAndUserDidntSelectSlot)
  }, [isInPast, startIsNotInQueryAndUserDidntSelectSlot])

  useEffect(() => {
    if (data && shouldCheckSlot && !userSelectedSlotManually && !isLoading && !practitionersLoading) {
      /**
       * The result here. as we are requesting start and end of a single slot,
       * will be an array with only one record most of the times...
       */
      if (data.length > 0) {
        const day = start.format('YYYY-MM-DD')
        const time = start.format('HH:mm:ss')

        // The server response is grouped by date, so, here we find the availabilities
        // group that matches the requested start time.
        const availabilitiesForTheRequestedDay = data.find((availabilities) => availabilities.date === day)

        // The list of possible appointment for the requested day
        const availabilities = availabilitiesForTheRequestedDay ? availabilitiesForTheRequestedDay.availabilities : []

        // The appointment who matches the requested time
        const desiredAvailability = availabilities.find((availability) => {
          return availability.start === time
        })

        if (desiredAvailability) {
          rescheduleDispatch(setSlotStatus(SlotStatus.AVAILABLE))
        } else {
          rescheduleDispatch(setSlotStatus(SlotStatus.UNAVAILABLE))
          setShouldRedirect(true)
        }
      } else if (!queryParamAppointment?.start && !userSelectedSlotManually) {
        /**
         * This is the case when the user has not chosen any of the provided slots in the email
         * and instead has chosen to click the button th schedule a new appointment.
         * In this case there is not start time in the query parameters. This is why will not try validate
         * the slot but instead will silently forward the user to choose a slot.
         */
        rescheduleDispatch(setSlotStatus(SlotStatus.UNAVAILABLE))
        setShouldRedirect(true)
      } else {
        /**
         * This will hit if the requested slot was unavailable
         */
        rescheduleDispatch(setSlotStatus(SlotStatus.UNAVAILABLE))
        setShouldRedirect(true)
      }
    } else if (shouldCheckSlot && userSelectedSlotManually) {
      /**
       * This looks like a workaround but is actually true,
       * if user has selected a slot this means that the slot
       * was given from appontments screen and forwarded back
       * for confirmation => the check was supposed to be done
       * form Core
       */
      rescheduleDispatch(setSlotStatus(SlotStatus.AVAILABLE))
    }
  }, [
    data,
    start,
    queryParamAppointment.start,
    shouldCheckSlot,
    userSelectedSlotManually,
    rescheduleDispatch,
    setShouldRedirect,
    queryParamAppointment,
    preferredPractitionerReferenceIds,
    practitionersLoading,
    isLoading,
  ])

  return { isLoading: practitionersLoading || isLoading, shouldRedirect }
}

export default useValidateSelectedSlot
